import React, { useRef, useState } from 'react';
import initializeFirebase from '../../../firebaseConfig';
import { increment, push, ref, update } from 'firebase/database';
import { getCurrentUTCTimestamp } from '../../../utils/utils';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { commentGifUploadThumbnail, commentImageUploadThumbnail } from '../../VideoPlayer/service';
import { useTranslateText } from '../../../Components/Multilanguage/Translate';
import Translate from '../../../Components/Multilanguage/Translate';
import GiphySearchbox from 'react-giphy-searchbox';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import Gif from '@mui/icons-material/Gif';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

const LiveChatReplyField = ({ setIsReplyField, childReplyFieldHandler, data, projectInfo, isEvent = false }) => {
  const user = useSelector((state) => state?.user?.value);
  const accessToken = useSelector((state) => state?.accessToken?.value);

  const appInfo = { projectDetails: projectInfo, accessToken };
  const gifApiKey = projectInfo?.projectConfig?.config?.GIPHY_API_KEY;
  const translateText = useTranslateText();
  const fileInputRef = useRef(null);

  const [replyValue, setReplyValue] = useState('');
  const [selectedGif, setSelectedGif] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isGiphyOpen, setIsGiphyOpen] = useState(false);

  const onGifSelect = async (item) => {
    const gifUrl = item?.images?.fixed_width_small?.url;
    const response = await commentGifUploadThumbnail(gifUrl, appInfo);
    if (response?.data?.data) {
      setImageDimensions({
        height: parseInt(item.images.fixed_width_small.height),
        width: parseInt(item.images.fixed_width_small.width),
        url: response.data.data,
      });
      setSelectedGif(item);
      setImageFile(null);
      setImagePreview(null);
      setIsGiphyOpen(false);
    }
  };

  const handleFileChange = async (e) => {
    const allowedExtensions = /(\.jpg|\.jpeg|\.png|\.gif)$/i;
    if (!allowedExtensions.exec(e?.target?.files[0]?.name)) return;
    const files = e.target.files;
    setSelectedGif(null);
    const response = await commentImageUploadThumbnail(files[0], appInfo);
    if (response?.data?.data) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new window.Image();
        img.onload = () => {
          setImageDimensions({ width: img.width, height: img.height, url: response.data.data });
        };
        img.src = reader.result;
        setImagePreview(reader.result);
      };
      setImageFile(files[0]);
      reader.readAsDataURL(files[0]);
      if (fileInputRef.current) fileInputRef.current.value = null;
    }
  };

  const handleRemove = () => {
    setSelectedGif(null);
    setImageFile(null);
    setImagePreview(null);
    setImageDimensions(null);
  };

  const handleSend = async () => {
    if (!replyValue.trim() && !selectedGif && !imageFile) {
      toast.error(<Translate textKey={'reply_field_required'} />, { position: 'top-center' });
      return;
    }
    const reply_data = {
      comment_text: replyValue.trim() || '',
      user_id: Number(user),
      created_at: getCurrentUTCTimestamp(),
    };
    if (imageDimensions) reply_data.image = imageDimensions;

    const db = initializeFirebase(projectInfo);
    const dbRefParent = ref(db, `comments/${isEvent ? 'event-' : ''}${data?.videoId}/${data?.id}`);
    update(dbRefParent, { reply_count: increment(1) });
    await push(ref(db, `comments/${isEvent ? 'event-' : ''}${data?.videoId}/${data?.id}/replies`), reply_data);

    setReplyValue('');
    handleRemove();
    setIsReplyField(false);
    if (childReplyFieldHandler) childReplyFieldHandler(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCancel = () => {
    setIsReplyField(false);
    if (childReplyFieldHandler) childReplyFieldHandler(false);
  };

  return (
    <div className="chatInputArea liveChatReplyField">
      {(selectedGif || imagePreview) && (
        <div className="chatMediaPreview">
          <button className="removeMediaBtn" onClick={handleRemove}>
            <CloseIcon fontSize="small" />
          </button>
          <img
            src={selectedGif ? selectedGif.images.fixed_height.url : imagePreview}
            alt="preview"
          />
        </div>
      )}

      <div className="chatInputRow">
        <input
          className="chatInput"
          type="text"
          placeholder={translateText('add_your_comment') || 'Reply…'}
          value={replyValue}
          onChange={(e) => setReplyValue(e.target.value)}
          onKeyDown={handleKeyDown}
          maxLength={500}
          autoFocus
        />
        <label className="mediaBtn" title="Upload image">
          <PhotoCamera fontSize="small" />
          <input type="file" accept="image/*" onChange={handleFileChange} ref={fileInputRef} />
        </label>
        <button className="mediaBtn gifBtn" title="Add GIF" onClick={() => setIsGiphyOpen(true)}>
          <Gif />
        </button>
        <button
          className="sendBtn"
          onClick={handleSend}
          disabled={!replyValue.trim() && !selectedGif && !imageFile}
          aria-label="Send reply"
        >
          <SendIcon fontSize="small" />
        </button>
      </div>

      {isGiphyOpen && (
        <div className="chatGiphyOverlay" onClick={(e) => { if (e.target === e.currentTarget) setIsGiphyOpen(false); }}>
          <div className="chatGiphyContent">
            <button className="closeGiphyBtn" onClick={() => setIsGiphyOpen(false)}>
              <CloseIcon />
            </button>
            <GiphySearchbox
              apiKey={gifApiKey}
              onSelect={onGifSelect}
              searchPlaceholder={translateText('search')}
              masonryConfig={[
                { columns: 2, imageWidth: 110, gutter: 5 },
                { mq: '700px', columns: 3, imageWidth: 120, gutter: 5 },
              ]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveChatReplyField;
